import React, { useState } from 'react';
import * as XLSX from 'xlsx';
import CheckIcon from '@material-ui/icons/Check';
import ClearIcon from '@material-ui/icons/Clear';
import actions from 'roles-actions-permissions/actions';
import usePermissions from 'hooks/permissions-hooks';
import Button from '@material-ui/core/Button';
import axiosClient from 'http/axios-client';
import { useAlerts } from 'hooks/alerts-hooks';
import MonthSelector from 'components/basic/MonthSelector';
import * as _ from 'lodash';
import { LoadingButtonNoGrid } from 'components/basic/LoadingButton';
import * as moment from 'moment';
import { SearchAndFilterButtons } from 'components/basic/SearchAndFilterButtons';
import { approvalApply } from 'utils/reportsUtils';
import { AiOutlineLineChart } from 'react-icons/ai';
import HasAnswersHook from 'hooks/hasAnswers-hook';
import { getFullRegistrationStatus, parseDate } from '../consents/ConsentsTable';
import { roles } from '../../../cassandra-shared-js/roles';
import { filterTypes } from '../../basic/AdvancedFilters';
import AppTable from '../../basic/AppTable';
import AppLoader from '../../basic/AppLoader';
import { useItemsReports } from '../../../hooks/atoms/items-hook-reports';
import CollectionKeys from '../../../hooks/molecules/collection-keys';
import getTheme from '../../../theme/theme';
import CollectionItemHeader from '../../layout/CollectionItemHeader';

const downloadData = async (userHasRole, setDownloadAllLoading, filterQuery) => {
  setDownloadAllLoading(true);
  const theKits = await axiosClient.get(CollectionKeys.REGISTRATION_KITS_FOR_REPORTS, {
    params: {
      ...filterQuery,
    },
  });

  if (null == theKits) {
    setDownloadAllLoading(false);
    return;
  }

  setDownloadAllLoading(false);

  const getRealStatus = (status) => {
    if (false === status) {
      return 'false';
    }
    if (true === status) {
      return 'true';
    }
    return 'pending';
  };

  const data = theKits.map((kit) => {
    const obj = {
      'Serial Number': kit.serial_number_id?.serial ?? '',
      'Report delivery date': parseDate(kit.report_delivery_date) ?? '',
      'Thematic Package': kit.thematic_package?.thematic_category_id?.name ?? '',
      'Report language': kit.locale ?? '',
      // TODO: You should show this..
      // 'Questionnaire Availability': `${ hasQuestionnaireObj[kit.serial_number_id.serial] }` ?? 'false',
      'Genotype Data Available': `${ true === kit?.health_data_available }`,
      'Polygenic Risk Available': `${ true === kit?.polygenic_risk_available }`,
      'PDF Available': `${ true === kit?.pdf_available }`,
      'Dynamic Report Available': `${ true === kit?.dynamic_report_available }`,
      'Bioinformatic Status': getRealStatus(kit?.analysis_approval),
      'Scientific Status 2': getRealStatus(kit?.third_party_approval_2),
      'Medical Status': getRealStatus(kit?.cardiologist_approval),
      'Registration Status': getFullRegistrationStatus(kit),
    };

    if (!userHasRole(roles.ADMINISTRATOR_ROLE)) {
      delete obj.locale;
    }

    if (userHasRole(roles.ANALYTICS_DEPARTMENT_USER_ROLE)) {
      delete obj['Questionnaire Availability'];
      delete obj['Genotype Data Available'];
      delete obj['Polygenic Risk Available'];
      delete obj['PDF Available'];
      delete obj['Dynamic Report Available'];
      delete obj['Scientific Status'];
      delete obj['Medical Status'];
      delete obj['Registration Status'];
    } else if (userHasRole(roles.QUALITY_DEPARTMENT_USER_ROLE) || userHasRole(roles.QUALITY_DEPARTMENT_USER_ROLE_2)) {
      delete obj['Questionnaire Availability'];
      delete obj['Genotype Data Available'];
      delete obj['Polygenic Risk Available'];
      delete obj['PDF Available'];
      delete obj['Dynamic Report Available'];
      delete obj['Medical Status'];
      delete obj['Registration Status'];
    } else if (userHasRole(roles.THIRD_PARTY_CARDIOLOGIST)) {
      delete obj['Thematic Package'];
      delete obj['Questionnaire Availability'];
      delete obj['Genotype Data Available'];
      delete obj['Polygenic Risk Available'];
      delete obj['PDF Available'];
      delete obj['Dynamic Report Available'];
      delete obj['Scientific Status'];
      delete obj['Bioinformatic Status'];
      delete obj['Registration Status'];
    }
    return obj;
  });

  const worksheet = XLSX.utils.json_to_sheet(data);
  worksheet['!cols'] = fitToColumn(data);

  function fitToColumn(arrayOfArray) {
    if (!arrayOfArray[0]) { // If file is empty
      return null;
    }
    return Object.keys(arrayOfArray[0]).map((key) => {
      return { wch: key.length };
    });
  }

  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, 'Sheet1');
  XLSX.writeFile(workbook, 'Registration Kits.xlsx');
};

async function multipleApprove(setApprovalLoading, filterQuery, client, selected, url, field, setShowLoader, refetch, setSelected, value = true) {
  const toBePosted = [];

  setApprovalLoading(true);
  const kits = await client.get(CollectionKeys.REGISTRATION_KITS_FOR_REPORTS, {
    params: {
      ...filterQuery,
    },
  });

  if (null == kits) {
    setApprovalLoading(false);
    return;
  }
  setApprovalLoading(false);

  selected.forEach((id) => {
    const kit = kits.find((theKit) => {
      return theKit.id === id;
    });

    if (kit === undefined || 'done' === kit.registrationStatus || true !== kit.pdf_available) { return; }

    if ('analysis_approval' === field && value === kit.analysis_approval) {
      return;
    }

    if ('third_party_approval_2' === field && (true !== kit.analysis_approval || value === kit.third_party_approval_2)) {
      return;
    }

    if ('cardiologist_approval' === field && (true !== kit.third_party_approval_2 || value === kit.cardiologist_approval)) {
      return;
    }

    toBePosted.push(kit.serial_number_id.serial);
  });

  if (0 === toBePosted.length) {
    return;
  }

  setShowLoader(true);

  if ('cardiologist_approval' === field) {
    const promises = toBePosted.map((serial) => {
      return client.put(`${ url }/${ serial }`, {
        [field]: value,
      });
    });

    Promise.all([ ...promises ])
      .finally(() => {
        Promise.all([ refetch() ]).finally(() => {
          setSelected([]);
          setShowLoader(false);
        });
      });
  } else {
    const requestBody = toBePosted.map(((serial) => {
      return {
        serial,
        [field]: value,
      };
    }));

    client.put(url, requestBody)
      .catch((err) => {
        console.error(err);
      })
      .finally(() => {
        refetch()
          .then(() => {
            setSelected([]);
            setShowLoader(false);
          });
      });
  }
}

const RestartProcess = ({ serial, url, setShowLoader, refetch, enabled, method, setSelected, disabledButton }) => {
  const { alertError } = useAlerts();

  return (
    <div style={ { display: 'flex', justifyContent: 'center' } }>
      {
        enabled && (
          <Button
            variant='outlined'
            disabled={ disabledButton }
            color='secondary'
            onClick={ () => {
              setShowLoader(true);
              axiosClient[method](`${ url }/${ serial }`)
                .catch((error) => {
                  if (error.response.data?.data?.code) {
                    if ('questionnaireNotExists' === error.response.data.data.code) {
                      alertError('Questionnaire doesn\'t exist.');
                    } else if ('registrationKitNotExist' === error.response.data.data.code) {
                      alertError('Registration Kit doesn\t exist.');
                    } else if ('serialNumberNotExist' === error.response.data.data.code) {
                      alertError('Serial number doesn\t exist.');
                    } else if ('allPRSAreUpdated' === error.response.data.data.code) {
                      alertError('All PRS are updated.');
                    } else {
                      console.log('not found', error.response.data.data);
                    }
                  } else {
                    console.log('no data', error);
                  }
                })
                .finally(() => {
                  refetch().then(() => {
                    setSelected([]);
                    setShowLoader(false);
                  });
                });
            } }
          >
            Restart process
          </Button>
        )
      }
    </div>
  );
};

const OtherReportsTable = () => {
  const [ selected, setSelected ] = useState([]);
  const [ filterQuery, setFilterQuery ] = useState({ });
  const [ loading, setLoading ] = useState(false);
  const [ selectedMonths, setSelectedMonths ] = useState(1);

  const handleChange = (event) => { setSelectedMonths(event.target.value); };

  const startDate = moment().subtract(selectedMonths, 'months').startOf('day').toISOString();

  const [ downloadAllLoading, setDownloadAllLoading ] = useState(false);
  const [ downloadLoading, setDownloadLoading ] = useState(false);
  const [ approvalLoading, setApprovalLoading ] = useState(false);

  let kitsFilterQuery = {
    ...filterQuery,
    'thematic_package.thematic_category_id.name_eq': 'Cardio Health',
    _where: [
      { _or:
        filterQuery?._where?._or },

      { _or: [
        [{ 'serial_number_id.report_payment_status_eq': 'paid' }],
        [{ 'serial_number_id.report_payment_status_eq': 'free' }],
      ] },
    ],
  };

  let allKitsFilterQuery = {
    ...filterQuery,
    'thematic_package.thematic_category_id.name_eq': 'Cardio Health',
    _limit: -1,
    _where: [
      { _or:
        filterQuery?._where?._or },

      { _or: [
        [{ 'serial_number_id.report_payment_status_eq': 'paid' }],
        [{ 'serial_number_id.report_payment_status_eq': 'free' }],
      ] },
    ],
  };

  const { hasPermissions, userHasRole, userHasRoleExact } = usePermissions();

  if (hasPermissions(actions.GENERAL_REPORT_FINALIZE_EDIT)) {
    kitsFilterQuery = {
      ...kitsFilterQuery,
      third_party_approval_2_eq: true,
      pdf_available_eq: true,
      'serial_number_id.ancestor_null': true,
    };

    allKitsFilterQuery = {
      ...allKitsFilterQuery,
      third_party_approval_2_eq: true,
      pdf_available_eq: true,
      'serial_number_id.ancestor_null': true,
    };
  } else if (hasPermissions(actions.ANALYTICS_REPORT_FINALIZE_EDIT)) {
    kitsFilterQuery = {
      ...kitsFilterQuery,
      pdf_available_eq: true,
      'serial_number_id.ancestor_null': true,
    };

    allKitsFilterQuery = {
      ...allKitsFilterQuery,
      pdf_available_eq: true,
      'serial_number_id.ancestor_null': true,
    };
  } else if (userHasRoleExact(roles.QUALITY_DEPARTMENT_USER_ROLE_2)) {
    kitsFilterQuery = {
      ...kitsFilterQuery,
      pdf_available_eq: true,
      analysis_approval_eq: true,
      'serial_number_id.ancestor_null': true,
    };

    allKitsFilterQuery = {
      ...allKitsFilterQuery,
      pdf_available_eq: true,
      analysis_approval_eq: true,
      'serial_number_id.ancestor_null': true,
    };
  }

  const {
    items: kits,
    itemsLoading: kitsLoading,
    pagination,
    refetch,
  } = useItemsReports(CollectionKeys.REGISTRATION_KITS_FOR_REPORTS, {
    filterQuery: kitsFilterQuery,
  });

  // query for answered questionnaires
  const { hasAnswers, hasAnswersIsLoading } = HasAnswersHook(kits);

  const {
    items: allKits,
    itemsLoading: allKitsLoading,
    refetch: allKitsRefetch,
    countFiltered,
  } = useItemsReports(CollectionKeys.REGISTRATION_KITS_FOR_REPORTS, {
    filterQuery: allKitsFilterQuery,
  });

  if (kitsLoading || allKitsLoading) {
    return <AppLoader />;
  }

  let tableColumns = [
    { id: 1, label: 'Serial Number', field: 'serial_number_id.serial' },
    { id: 220,
      label: 'Report delivery date',
      field: 'report_delivery_date',
      customRenderer({ row }) {
        return (
          <>
            {parseDate(row.report_delivery_date)}
          </>
        );
      } },
    { id: 2, label: 'Thematic Package', field: 'thematic_package.thematic_category_id.name', noSort: true },
    { id: 3, label: 'Download Report', field: 'pdf_available', downloadReport: true },
    ...(userHasRole(roles.ADMINISTRATOR_ROLE)) ? [{
      id: 931,
      label: 'Report language',
      noSort: false,
      field: 'locale',
      kitLang: true,
    }] : [],
    { id: 40,
      label: 'Questionnaire Availability',
      field: 'pdf_available',
      noSort: true,
      customRenderer({ row }) {
        const { serial } = row.serial_number_id;
        return (
          <>
            {!hasAnswersIsLoading && hasAnswers[serial] ? (
              <div style={ { display: 'flex', justifyContent: 'center' } }>
                <CheckIcon
                  style={ { color: '#93C120' } }
                />
              </div>
            ) : (
              <div style={ { display: 'flex', justifyContent: 'center' } }>
                <ClearIcon style={ { color: 'red' } } />
              </div>
            )}
          </>
        );
      } },
    { id: 5, label: 'Genotype Data Available', field: 'health_data_available', doneOrButton: true },
    { id: 6, label: 'Polygenic Risk Available', field: 'polygenic_risk_available', doneOrButton: true },
    { id: 4, label: 'PDF Available', field: 'pdf_available', doneOrButton: true },
    { id: 7, label: 'Dynamic Report Available', field: 'dynamic_report_available', doneOrButton: true },
    { id: 90,
      label: 'Genotype Algorithm',
      field: 'registrationStatus',
      noSort: true,
      customRenderer({
        setShowLoader,
        refetch: theRefetch,
        row,
      }) {
        const enabled = true;

        let disabledButton = false;
        if (userHasRole(roles.ANALYTICS_DEPARTMENT_USER_ROLE) && false !== row.analysis_approval) {
          disabledButton = true;
        }

        const { serial } = row.serial_number_id;

        return (
          <RestartProcess
            enabled={ enabled }
            serial={ serial }
            url='registration-kits/reportProcess'
            method='post'
            setShowLoader={ setShowLoader }
            refetch={ theRefetch }
            setSelected={ setSelected }
            disabledButton={ 'done' === row.registrationStatus || disabledButton }
          />
        );
      } },
  ];

  if (hasPermissions(actions.GENERAL_REPORT_FINALIZE_EDIT)) {
    tableColumns = [
      { id: 1, label: 'Serial Number', field: 'serial_number_id.serial' },
      { id: 3, label: 'Download Report', field: 'pdf_available', downloadReport: true, noSort: true },
    ];
  }

  if (hasPermissions(actions.ANALYTICS_REPORT_FINALIZE_VIEW)) {
    tableColumns.push(
      { id: 9,
        label: 'Bioinformatic Status',
        field: 'analysis_approval',
        noSort: false,
        customRenderer({
          row,
        }) {
          const disabled = false === row.analysis_approval && 'done' !== row.registrationStatus;

          let value = '';
          if (null === row.analysis_approval) {
            value = 'Pending';
          } else if (!disabled) {
            value = 'Approved';
          } else {
            value = 'Disapproved';
          }

          return (
            <div style={ { display: 'flex', justifyContent: 'flex-start' } }>
              {value}
            </div>
          );
        } },
      ...hasPermissions(actions.ANALYTICS_REPORT_FINALIZE_EDIT)
        ? [{ id: 79,
          label: 'Bioinformatic Approval',
          field: 'analysis_approval',
          noSort: true,
          customRenderer({
            setShowLoader,
            refetch: theRefetch,
            row,
          }) {
            const { serial } = row.serial_number_id;

            if (true !== row.pdf_available || 'done' === row.registrationStatus) {
              return <div />;
            }

            return (
              <div style={ { display: 'flex', justifyContent: 'center', gap: '5px' } }>
                {(null === row.analysis_approval || false === row.analysis_approval) && 'done' !== row.registrationStatus && (

                  <Button
                    variant='outlined'
                    color='secondary'
                    disabled={ !hasPermissions(actions.ANALYTICS_REPORT_FINALIZE_EDIT) }
                    onClick={ () => { approvalApply(`registration-kits/analysis-update/${ serial }`, 'analysis_approval', true, setShowLoader, theRefetch, allKitsRefetch, setSelected); } }
                  >
                    APPROVE
                  </Button>
                )}
                {null === row.analysis_approval && 'done' !== row.registrationStatus && (
                  <Button
                    disabled={ !hasPermissions(actions.ANALYTICS_REPORT_FINALIZE_EDIT) }
                    style={ { color: !hasPermissions(actions.ANALYTICS_REPORT_FINALIZE_EDIT) ? '' : 'red',
                      borderColor: !hasPermissions(actions.ANALYTICS_REPORT_FINALIZE_EDIT) ? 'rgba(0, 0, 0, 0.26)' : 'red' } }
                    variant='outlined'
                    onClick={ () => { approvalApply(`registration-kits/analysis-update/${ serial }`, 'analysis_approval', false, setShowLoader, theRefetch, allKitsRefetch, setSelected); } }
                  >
                    DISAPPROVE
                  </Button>

                )}
              </div>
            );
          } }] : [],
    );
  }

  if (hasPermissions(actions.THIRD_PARTY_REPORT_FINALIZE_VIEW)) {
    tableColumns.push(
      { id: 4919,
        label: 'Scientific Status 2',
        field: 'third_party_approval_2',
        noSort: false,
        customRenderer({
          row,
        }) {
          const disabled = (false === row.third_party_approval_2) && 'done' !== row.registrationStatus;

          let value = '';
          if (null === row.third_party_approval_2) {
            value = 'Pending';
          } else if (!disabled) {
            value = 'Approved';
          } else {
            value = 'Disapproved';
          }

          return (
            <div style={ { display: 'flex', justifyContent: 'center' } }>
              {value}
            </div>
          );
        } },
      ...(hasPermissions(actions.THIRD_PARTY_REPORT_FINALIZE_EDIT) && userHasRoleExact(roles.QUALITY_DEPARTMENT_USER_ROLE_2))
        ? [{ id: 23459,
          label: 'Scientific Approval 2',
          field: 'third_party_approval_2',
          noSort: true,
          customRenderer({
            setShowLoader,
            refetch: theRefetch,
            row,
          }) {
            const { serial } = row.serial_number_id;

            if (false === row.analysis_approval || true !== row.pdf_available
              || null === row.analysis_approval || 'done' === row.registrationStatus) {
              return null;
            }

            return (
              <div style={ { display: 'flex', justifyContent: 'center', gap: '5px' } }>
                { (null === row.third_party_approval_2 || false === row.third_party_approval_2) && 'done' !== row.registrationStatus && (
                  <Button
                    variant='outlined'
                    color='secondary'
                    disabled={ !hasPermissions(actions.THIRD_PARTY_REPORT_FINALIZE_EDIT) }
                    onClick={ () => { approvalApply(`registration-kits/third-party-update/${ serial }`, 'third_party_approval_2', true, setShowLoader, theRefetch, allKitsRefetch, setSelected); } }
                  >
                    APPROVE
                  </Button>
                )}
                {null === row.third_party_approval_2 && 'done' !== row.registrationStatus && (
                  <Button
                    variant='outlined'
                    color='secondary'
                    disabled={ !hasPermissions(actions.THIRD_PARTY_REPORT_FINALIZE_EDIT) }
                    style={ { color: !hasPermissions(actions.THIRD_PARTY_REPORT_FINALIZE_EDIT) ? '' : 'red',
                      borderColor: !hasPermissions(actions.THIRD_PARTY_REPORT_FINALIZE_EDIT) ? 'rgba(0, 0, 0, 0.26)' : 'red' } }
                    onClick={ () => { approvalApply(`registration-kits/third-party-update/${ serial }`, 'third_party_approval_2', false, setShowLoader, theRefetch, allKitsRefetch, setSelected); } }
                  >
                    DISAPPROVE
                  </Button>
                )}
              </div>
            );
          } }] : [],
    );
  }

  if (userHasRole(roles.THIRD_PARTY_CARDIOLOGIST) || userHasRole(roles.ADMINISTRATOR_ROLE) || userHasRole(roles.ANALYTICS_DEPARTMENT_USER_ROLE)) {
    tableColumns.push(
      { id: 1239,
        label: 'Chief Scientific Officer Approval Status',
        field: 'cardiologist_approval',
        noSort: false,
        customRenderer({
          row,
        }) {
          const disabled = false === row.cardiologist_approval && 'done' !== row.registrationStatus;

          let value = '';
          if (null === row.cardiologist_approval) {
            value = 'Pending';
          } else if (!disabled) {
            value = 'Approved';
          } else {
            value = 'Disapproved';
          }

          return (
            <div style={ { display: 'flex', justifyContent: 'flex-start' } }>
              {value}
            </div>
          );
        } },
      ...userHasRole(roles.THIRD_PARTY_CARDIOLOGIST)
        ? [{ id: 12579,
          label: 'Chief Scientific Officer Approval',
          field: 'cardiologist_approval',
          noSort: true,
          customRenderer({
            setShowLoader,
            refetch: theRefetch,
            row,
          }) {
            const { serial } = row.serial_number_id;

            if (true !== row.pdf_available || 'done' === row.registrationStatus) {
              return <div />;
            }

            return (
              <div style={ { display: 'flex', justifyContent: 'center', gap: '5px' } }>
                { (null === row.cardiologist_approval || false === row.cardiologist_approval) && 'done' !== row.registrationStatus && (
                  <Button
                    variant='outlined'
                    color='secondary'
                    disabled={ !hasPermissions(actions.GENERAL_REPORT_FINALIZE_EDIT) }
                    onClick={ () => { approvalApply(`registration-kits/cardiologist-update/${ serial }`, 'cardiologist_approval', true, setShowLoader, theRefetch, allKitsRefetch, setSelected); } }
                  >
                    APPROVE
                  </Button>
                )}
                {null === row.cardiologist_approval && 'done' !== row.registrationStatus && (
                  <Button
                    disabled={ !hasPermissions(actions.GENERAL_REPORT_FINALIZE_EDIT) }
                    style={ { color: !hasPermissions(actions.GENERAL_REPORT_FINALIZE_EDIT) ? '' : 'red',
                      borderColor: !hasPermissions(actions.GENERAL_REPORT_FINALIZE_EDIT) ? 'rgba(0, 0, 0, 0.26)' : 'red' } }
                    variant='outlined'
                    onClick={ () => { approvalApply(`registration-kits/cardiologist-update/${ serial }`, 'cardiologist_approval', false, setShowLoader, theRefetch, allKitsRefetch, setSelected); } }
                  >
                    DISAPPROVE
                  </Button>
                )}
              </div>
            );
          } }] : [],
    );
  }

  if (!hasPermissions(actions.GENERAL_REPORT_FINALIZE_EDIT)
  && !userHasRole(roles.ANALYTICS_DEPARTMENT_USER_ROLE)
  && !userHasRole(roles.QUALITY_DEPARTMENT_USER_ROLE)
&& !userHasRole(roles.QUALITY_DEPARTMENT_USER_ROLE_2)
  ) {
    tableColumns.push(
      { id: 15,
        label: 'Registration Status',
        field: 'registrationStatus',
        noSort: true,
        customRenderer({ row }) {
          return (
            <>
              {
                getFullRegistrationStatus(row)
              }
            </>
          );
        } },
    );
  }

  return (
    <div>
      <div style={ { display: 'flex', flexDirection: 'row', alignItems: 'center', marginBottom: getTheme().spacing(2) } }>
        <AiOutlineLineChart
          style={ { color: getTheme().palette.secondary.dark, marginRight: getTheme().spacing(2) } }
          size={ 36 }
        />
        <CollectionItemHeader
          title='Cardio Health Reports'
          subtitle={ `Entries: ${ countFiltered ?? 0 }` }
          actions={ (
            <>
            </>
          ) }
        />
      </div>
      <div style={ { display: 'flex', gap: '5px', marginBottom: '15px' } }>
        <span>
          <Button
            variant='contained'
            color='secondary'
            onClick={ () => {
              setSelected(allKits?.map((ar) => { return ar.id; }));
            } }
          >
            Select All
          </Button>
        </span>
        <span>
          <Button
            variant='contained'
            color='secondary'
            onClick={ () => {
              setSelected([]);
            } }
          >
            Clear Selection
          </Button>
        </span>
        {
          (userHasRole(roles.ANALYTICS_DEPARTMENT_USER_ROLE) || userHasRole(roles.QUALITY_DEPARTMENT_USER_ROLE_2) || userHasRole(roles.THIRD_PARTY_CARDIOLOGIST)) && (
            <LoadingButtonNoGrid
              variant='contained'
              color='secondary'
              loading={ approvalLoading }
              onClick={ () => {
                if (userHasRole(roles.ANALYTICS_DEPARTMENT_USER_ROLE)) {
                  multipleApprove(setApprovalLoading, allKitsFilterQuery, axiosClient, selected, 'registration-kits/analysis-batch-update', 'analysis_approval', setLoading, refetch, setSelected);
                } else if (userHasRole(roles.QUALITY_DEPARTMENT_USER_ROLE_2)) {
                  multipleApprove(setApprovalLoading, allKitsFilterQuery, axiosClient, selected, 'registration-kits/third-party-batch-update', 'third_party_approval_2', setLoading, refetch, setSelected);
                } else {
                  multipleApprove(setApprovalLoading, allKitsFilterQuery, axiosClient, selected, 'registration-kits/cardiologist-update', 'cardiologist_approval', setLoading, refetch, setSelected);
                }
              } }
              label='Approve selected'
            />
          )
        }
        {
          (userHasRole(roles.ANALYTICS_DEPARTMENT_USER_ROLE) || userHasRole(roles.QUALITY_DEPARTMENT_USER_ROLE_2) || userHasRole(roles.THIRD_PARTY_CARDIOLOGIST)) && (
            <LoadingButtonNoGrid
              variant='contained'
              color='secondary'
              loading={ approvalLoading }
              onClick={ () => {
                if (userHasRole(roles.ANALYTICS_DEPARTMENT_USER_ROLE)) {
                  multipleApprove(setApprovalLoading, allKitsFilterQuery, axiosClient, selected, 'registration-kits/analysis-batch-update', 'analysis_approval', setLoading, refetch, setSelected, false);
                } else if (userHasRole(roles.QUALITY_DEPARTMENT_USER_ROLE_2)) {
                  multipleApprove(setApprovalLoading, allKitsFilterQuery, axiosClient, selected, 'registration-kits/third-party-batch-update', 'third_party_approval_2', setLoading, refetch, setSelected, false);
                } else {
                  multipleApprove(setApprovalLoading, allKitsFilterQuery, axiosClient, selected, 'registration-kits/cardiologist-update', 'cardiologist_approval', setLoading, refetch, setSelected, false);
                }
              } }
              label='Disapprove selected'
            />
          )
        }

        <LoadingButtonNoGrid
          variant='contained'
          onClick={ () => { downloadData(userHasRole, setDownloadLoading, kitsFilterQuery); } }
          label='Download'
          loading={ downloadLoading }
        />
        <LoadingButtonNoGrid
          loading={ downloadAllLoading }
          variant='contained'
          label='Download Last'
          onClick={ () => {
            if (selectedMonths === 'All') {
              downloadData(userHasRole, setDownloadAllLoading, allKitsFilterQuery);
            } else {
              downloadData(userHasRole, setDownloadAllLoading, { ...allKitsFilterQuery, _limit: -1, created_at_gte: startDate });
            }
          } }
        />

        <MonthSelector selectedMonths={ selectedMonths } handleChange={ handleChange } />
        {
          (userHasRole(roles.THIRD_PARTY_CARDIOLOGIST)
          || userHasRole(roles.QUALITY_DEPARTMENT_USER_ROLE_2) || userHasRole(roles.ANALYTICS_DEPARTMENT_USER_ROLE)) && (
            <SearchAndFilterButtons
              setFilterQuery={ setFilterQuery }
              filterQuery={ filterQuery }
              reportType='CARDIO'
            />
          )
        }

      </div>
      <AppTable
        columns={ tableColumns }
        data={ kits }
        loading={ kitsLoading || loading || allKitsLoading }
        paginationProps={ pagination }
        refetch={ refetch }
        advancedFiltersProps={
          userHasRole(roles.ANALYTICS_DEPARTMENT_USER_ROLE) || userHasRole(roles.QUALITY_DEPARTMENT_USER_ROLE) || userHasRole(roles.QUALITY_DEPARTMENT_USER_ROLE_2)
          || userHasRole(roles.THIRD_PARTY_CARDIOLOGIST)
            ? null
            : {
              properties: [
                {
                  value: 'serial_number_id.serial',
                  label: 'Serial Number',
                  type: filterTypes.FREE_TEXT,
                },
                {
                  value: 'analysis_approval',
                  label: 'Analysis approval',
                  type: filterTypes.CATEGORY,
                  options: [
                    { value: false, label: 'FALSE' },
                    { value: true, label: 'TRUE' },
                  ],
                },
                {
                  value: 'third_party_approval',
                  label: 'Scientific Approval',
                  type: filterTypes.CATEGORY,
                  options: [
                    { value: false, label: 'FALSE' },
                    { value: true, label: 'TRUE' },
                  ],
                },
                {
                  label: 'Registration Status ', value: 'registrationStatus', isRegistration: true, type: filterTypes.FREE_TEXT,
                },
                {
                  value: 'child_id',
                  label: 'Children',
                  type: filterTypes.CATEGORY,
                  options: [
                    { value: false, label: 'True' },
                    { value: true, label: 'False' },
                  ],
                },
              ],
              initialAndFilterOptions: [],
              onQueryChange: setFilterQuery,
            }
        }
        selectionProps={ { setSelected, selected } }
      />
    </div>
  );
};

export default OtherReportsTable;
